import moment from 'moment-timezone';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';

import { 
  MembershipSchema, MessageSchema, GroupedMessageListType, 
  FilterOptionSchema, PaymentStatusType, ResourceType, PlanType,
  MyBusinessSchema, TimeBlock, TimeSlot, ResourceSchema, 
  ResourceBookingSchema, IdpAccountRequestSchema, BookingStatusType,
   PlanSchema, ResourcePlanSchema, TeamMemberSchema,
   BookingFilterState, BookingSortDirection, BookingSortField,
   MemberResourceBookingPlanIdsMap, MemberResourceBookingBalanceMap,
   MemberResourceBookingPlanIdsJsonString, MemberResourceBookingBalanceJsonString,
   EMPTY_MEMBER_BOOKING_PLAN_IDS_JSON, EMPTY_MEMBER_BOOKING_BALANCES_JSON,
   MemberInventoryLimitsMap, MemberInventoryLimitsJsonString, EMPTY_MEMBER_INVENTORY_LIMITS_JSON,
   PeriodType, ResourceOptionType, BookingRecurrenceType, ClosedDatesType, CredentialExpirationInfo, CurrentRequirementsSchema,
   InventorySchema, PlanInventorySelection, PlanResourceAssociation, ResourceInventorySchema,
   SubscriptionSchema, SubscriptionRecurrenceType } from '../data/types'
import { isContactBuilderResource } from './contactBuilder'
import { ReactComponent as VisaCardIcon } from '../icons/visaCardIcon.svg'
import { ReactComponent as MasterCardIcon } from '../icons/masterCardIcon.svg'
import { ReactComponent as AmexCardIcon } from '../icons/amexCardIcon.svg'

import inventoryCatalog from '../data/inventoryCatalog.json';
import { User } from "firebase/auth";
import { usesReferralHandoff } from './fundingPartners';
import { USStatesEnum } from './enums';


const TIMEZONE = 'America/New_York';

/** Maps a US state abbreviation or full name to its 2-letter code for form selects. */
export const resolveUsStateAbbreviation = (value?: string): string => {
  const trimmed = (value || '').trim();
  if (!trimmed) return '';
  const upper = trimmed.toUpperCase();
  if (USStatesEnum[upper]?.abbreviation) return USStatesEnum[upper].abbreviation;
  const match = Object.values(USStatesEnum).find(
    (entry) => entry.fullName.toLowerCase() === trimmed.toLowerCase()
  );
  return match?.abbreviation || '';
};

export const getBookingDisplayDate = (booking: ResourceBookingSchema): string | undefined => {
  const resourceType = booking?.resource_type || '';
  switch (resourceType) {
    case 'kitchen':
    case 'training':
    case 'expert':
      return booking.start_time;
    default:
      return booking.start_time || booking.created;
  }
};

export const getBookingDurationHours = (booking: ResourceBookingSchema): number => {
  if (booking.total_hours != null && !Number.isNaN(booking.total_hours)) {
    return booking.total_hours;
  }
  if (booking.start_time && booking.end_time) {
    return moment(booking.end_time).diff(moment(booking.start_time), 'hours', true);
  }
  return 0;
};

const parseNumericFilter = (value: string): number | null => {
  if (!value.trim()) return null;
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const matchesNumericRange = (
  value: number | undefined,
  min: string,
  max: string
): boolean => {
  const minVal = parseNumericFilter(min);
  const maxVal = parseNumericFilter(max);
  const num = value ?? 0;
  if (minVal != null && num < minVal) return false;
  if (maxVal != null && num > maxVal) return false;
  return true;
};

export const filterBookings = (
  bookings: ResourceBookingSchema[],
  filters: BookingFilterState,
  searchTerm?: string
): ResourceBookingSchema[] => {
  const term = (searchTerm || filters.resourceName || '').trim().toLowerCase();

  return bookings.filter((booking) => {
    if (filters.statuses.length > 0) {
      if (!booking.status || !filters.statuses.includes(booking.status)) {
        return false;
      }
    }

    if (filters.resourceTypes.length > 0) {
      if (!booking.resource_type || !filters.resourceTypes.includes(booking.resource_type)) {
        return false;
      }
    }

    if (term) {
      const name = (booking.resource_name || '').toLowerCase();
      const summary = (booking.summary || '').toLowerCase();
      if (!name.includes(term) && !summary.includes(term)) {
        return false;
      }
    }

    const displayDate = getBookingDisplayDate(booking);
    if (filters.dateFrom && displayDate) {
      if (moment(displayDate).isBefore(moment(filters.dateFrom).startOf('day'))) {
        return false;
      }
    }
    if (filters.dateTo && displayDate) {
      if (moment(displayDate).isAfter(moment(filters.dateTo).endOf('day'))) {
        return false;
      }
    }

    if (!matchesNumericRange(booking.plan_rate, filters.planRateMin, filters.planRateMax)) {
      return false;
    }

    if (!matchesNumericRange(booking.total_cost, filters.totalCostMin, filters.totalCostMax)) {
      return false;
    }

    const duration = getBookingDurationHours(booking);
    if (!matchesNumericRange(duration, filters.durationMin, filters.durationMax)) {
      return false;
    }

    return true;
  });
};

export const filterMessageMemberships = (
  memberships: MembershipSchema[],
  searchTerm: string
): MembershipSchema[] => {
  const query = searchTerm.trim().toLowerCase();
  if (!query) return memberships;

  return memberships.filter((membership) => {
    const searchableText = [
      membership.name,
      membership.username,
      membership.community_id,
      membership.id,
      membership.team_name,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return searchableText.includes(query);
  });
};

const getSortValue = (
  booking: ResourceBookingSchema,
  field: BookingSortField
): string | number => {
  switch (field) {
    case 'resource_name':
      return (booking.resource_name || '').toLowerCase();
    case 'resource_type':
      return (booking.resource_type || '') as ResourceType;
    case 'status':
      return booking.status || '';
    case 'total_hours':
      return getBookingDurationHours(booking);
    case 'plan_rate':
      return booking.plan_rate ?? 0;
    case 'total_cost':
      return booking.total_cost ?? 0;
    case 'created':
      return booking.created ? moment(booking.created).valueOf() : 0;
    case 'start_time':
    default: {
      const date = getBookingDisplayDate(booking);
      return date ? moment(date).valueOf() : 0;
    }
  }
};

export const sortBookings = (
  bookings: ResourceBookingSchema[],
  field: BookingSortField,
  direction: BookingSortDirection
): ResourceBookingSchema[] => {
  const sorted = [...bookings].sort((a, b) => {
    const aVal = getSortValue(a, field);
    const bVal = getSortValue(b, field);

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return aVal.localeCompare(bVal);
    }
    return (aVal as number) - (bVal as number);
  });

  return direction === 'desc' ? sorted.reverse() : sorted;
};

export const formatBookingDateTime = (
  booking: ResourceBookingSchema,
  format = 'MM/DD/YYYY hh:mma'
): string => {
  const date = getBookingDisplayDate(booking);
  if (!date) return '—';
  const tz = booking.timezone || TIMEZONE;
  return moment(date).tz(tz).format(format);
};

export const formatBookingTimeRange = (booking: ResourceBookingSchema): string => {
  if (!booking.start_time) return '—';
  const tz = booking.timezone || TIMEZONE;
  const start = moment(booking.start_time).tz(tz).format('hh:mma');
  if (!booking.end_time) return start;
  const end = moment(booking.end_time).tz(tz).format('hh:mma');
  return `${start} - ${end}`;
};


/** Ensures each team member has a unique, stable id for selection UI. */
export const normalizeTeamMembers = (members: TeamMemberSchema[] = []): TeamMemberSchema[] => {
  const seenIds = new Set<string>();

  return members.map((member, index) => {
    const candidates = [
      member.id?.trim(),
      member.email?.trim(),
      member.username?.trim(),
      `team-${index}`,
    ].filter((value): value is string => Boolean(value));

    let id = candidates[0] ?? `team-${index}`;
    let suffix = 0;
    while (seenIds.has(id)) {
      suffix += 1;
      id = `${candidates[0] ?? `team-${index}`}-${suffix}`;
    }

    seenIds.add(id);
    return { ...member, id };
  });
};


export const formatDuration = (minutes: number): string => {
  if (minutes < 60) {
      return `${minutes} min`;
  } else if (minutes < 1440) { // Less than 24 hours
      const hours = Math.floor(minutes / 60);
      return `${hours} ${hours === 1 ? 'hr' : 'hrs'}`;
  } else if (minutes < 10080) { // Less than 7 days
      const days = Math.floor(minutes / 1440);
      return `${days} ${days === 1 ? 'day' : 'days'}`;
  } else if (minutes < 43200) { // Less than 30 days
      const weeks = Math.floor(minutes / 10080);
      return `${weeks} ${weeks === 1 ? 'week' : 'weeks'}`;
  } else { // 30+ days
      const months = Math.floor(minutes / 43200);
      return `${months} ${months === 1 ? 'month' : 'months'}`;
  }
};


/**
 * Parses a `MemberResourceBookingPlanIdsJsonString` into a resourceId → planId map.
 * Invalid / empty input safely returns `{}`.
 */
export const parseMemberResourceBookingPlanIds = (
  jsonString?: MemberResourceBookingPlanIdsJsonString | string | null
): MemberResourceBookingPlanIdsMap => {
  try {
    if (!jsonString || jsonString.trim() === '') {
      return {};
    }
    const parsed = JSON.parse(jsonString);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    return parsed as MemberResourceBookingPlanIdsMap;
  } catch (error) {
    if (process.env.NODE_ENV !== 'test') {
      console.error('Error parsing MemberResourceBookingPlanIdsMap from JSON:', error);
    }
    return {};
  }
}

/**
 * Parses a `MemberResourceBookingBalanceJsonString` into a resourceId → balance map.
 * Invalid / empty input safely returns `{}`.
 */
export const parseMemberResourceBookingBalances = (
  jsonString?: MemberResourceBookingBalanceJsonString | string | null
): MemberResourceBookingBalanceMap => {
  try {
    if (!jsonString || jsonString.trim() === '') {
      return {};
    }
    const parsed = JSON.parse(jsonString);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    return parsed as MemberResourceBookingBalanceMap;
  } catch (error) {
    if (process.env.NODE_ENV !== 'test') {
      console.error('Error parsing MemberResourceBookingBalanceMap from JSON:', error);
    }
    return {};
  }
}

/**
 * Serializes a booking plan ids map to the MembershipSchema JSON string form.
 */
export const stringifyMemberResourceBookingPlanIds = (
  memberResourceMap: MemberResourceBookingPlanIdsMap
): MemberResourceBookingPlanIdsJsonString => {
  try {
    return JSON.stringify(memberResourceMap) as MemberResourceBookingPlanIdsJsonString;
  } catch (error) {
    console.error('Error stringifying MemberResourceBookingPlanIdsMap:', error);
    return EMPTY_MEMBER_BOOKING_PLAN_IDS_JSON;
  }
}

/**
 * Serializes a booking balances map to the MembershipSchema JSON string form.
 */
export const stringifyMemberResourceBookingBalances = (
  memberResourceMap: MemberResourceBookingBalanceMap
): MemberResourceBookingBalanceJsonString => {
  try {
    return JSON.stringify(memberResourceMap) as MemberResourceBookingBalanceJsonString;
  } catch (error) {
    console.error('Error stringifying MemberResourceBookingBalanceMap:', error);
    return EMPTY_MEMBER_BOOKING_BALANCES_JSON;
  }
}

/**
 * Parses a `MemberInventoryLimitsJsonString` into an inventoryId → remaining qty map.
 * Invalid / empty input safely returns `{}`.
 */
export const parseMemberInventoryLimits = (
  jsonString?: MemberInventoryLimitsJsonString | string | null
): MemberInventoryLimitsMap => {
  try {
    if (!jsonString || jsonString.trim() === '') {
      return {};
    }
    const parsed = JSON.parse(jsonString);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    const limits: MemberInventoryLimitsMap = {};
    Object.entries(parsed).forEach(([inventoryId, rawQty]) => {
      if (typeof rawQty === 'boolean') {
        return;
      }
      const qty = typeof rawQty === 'number' ? rawQty : Number(rawQty);
      if (Number.isFinite(qty) && qty >= 0) {
        limits[String(inventoryId)] = qty;
      }
    });
    return limits;
  } catch (error) {
    if (process.env.NODE_ENV !== 'test') {
      console.error('Error parsing MemberInventoryLimitsMap from JSON:', error);
    }
    return {};
  }
}

/**
 * Serializes an inventory limits map to the MembershipSchema JSON string form.
 */
export const stringifyMemberInventoryLimits = (
  inventoryLimits: MemberInventoryLimitsMap
): MemberInventoryLimitsJsonString => {
  try {
    return JSON.stringify(inventoryLimits) as MemberInventoryLimitsJsonString;
  } catch (error) {
    console.error('Error stringifying MemberInventoryLimitsMap:', error);
    return EMPTY_MEMBER_INVENTORY_LIMITS_JSON;
  }
}

/**
 * Whether dual-plan inventory allowances should apply for this member + plan.
 */
export const shouldApplyMemberInventoryLimits = (
  plan: PlanSchema | ResourcePlanSchema | null | undefined,
  member?: MembershipSchema | null
): boolean => {
  if (plan?.plan_type !== 'dual') {
    return false;
  }
  const hasPlanInventory = (plan.associated_resources || []).some(
    (association) => (association.inventory || []).length > 0
  );
  const hasMemberLimits = Object.keys(parseMemberInventoryLimits(member?.inventory_limits)).length > 0;
  return hasPlanInventory || hasMemberLimits;
}

/**
 * Plan-configured inventory_id → included max qty for dual plans
 * (mirrors backend `_plan_inventory_limits`).
 */
export const getPlanInventoryLimitsMap = (
  plan: PlanSchema | ResourcePlanSchema | null | undefined
): MemberInventoryLimitsMap => {
  if (plan?.plan_type !== 'dual' || !plan.associated_resources?.length) {
    return {};
  }
  const limits: MemberInventoryLimitsMap = {};
  plan.associated_resources.forEach((association) => {
    (association.inventory || []).forEach((selection) => {
      if (!selection?.inventory_id) {
        return;
      }
      if (!Number.isFinite(selection.max_qty) || selection.max_qty < 0) {
        return;
      }
      const key = String(selection.inventory_id);
      limits[key] = (limits[key] ?? 0) + selection.max_qty;
    });
  });
  return limits;
}

/** Minimal ordered-inventory shape needed to aggregate consumed quantities. */
export type OrderedInventoryQuantity = {
  id?: string
  inventory_id?: string
  total_quantity?: number
}

/**
 * Remaining inventory allowances for booking cost display / edit repricing.
 * For approved bookings, restores this booking's consumed qty (capped at plan max)
 * to match backend `_inventory_limits_for_booking_pricing`.
 */
export const getInventoryLimitsForBookingPricing = (
  member: MembershipSchema | null | undefined,
  plan: PlanSchema | ResourcePlanSchema | null | undefined,
  booking?: {
    status?: string | null
    order_items?: OrderedInventoryQuantity[]
    order_inventory?: OrderedInventoryQuantity[]
  } | null
): MemberInventoryLimitsMap => {
  const configuredLimits = getPlanInventoryLimitsMap(plan);
  if (Object.keys(configuredLimits).length === 0) {
    return parseMemberInventoryLimits(member?.inventory_limits);
  }

  const memberLimits = parseMemberInventoryLimits(member?.inventory_limits);
  const limits: MemberInventoryLimitsMap =
    Object.keys(memberLimits).length > 0
      ? { ...memberLimits }
      : { ...configuredLimits };

  if (!booking || String(booking.status || '').toLowerCase() !== 'approved') {
    return limits;
  }

  const existingItems = booking.order_items ?? booking.order_inventory ?? [];
  const orderedByInventory: MemberInventoryLimitsMap = {};
  existingItems.forEach((item) => {
    const inventoryId = item.inventory_id || item.id;
    if (!inventoryId) {
      return;
    }
    orderedByInventory[inventoryId] =
      (orderedByInventory[inventoryId] ?? 0) + Math.max(0, item.total_quantity || 0);
  });

  Object.entries(orderedByInventory).forEach(([inventoryId, quantity]) => {
    if (!(inventoryId in configuredLimits)) {
      return;
    }
    limits[inventoryId] = Math.min(
      configuredLimits[inventoryId],
      (limits[inventoryId] ?? 0) + quantity
    );
  });

  return limits;
}

export type InventoryCostLineBreakdown = {
  inventoryId: string
  includedQty: number
  billableQty: number
  lineCost: number
  remainingAfterThis: number
  unitPrice: number
  totalQuantity: number
}

/**
 * Allowance-aware inventory line pricing matching backend `_calculate_inventory_cost`:
 * charge only `unit_price * max(0, qty - remaining_limit)`, consuming remaining across lines.
 */
export const calculateInventoryCostLinesWithLimits = (
  lines: Array<{
    inventoryId: string
    totalQuantity: number
    unitPrice?: number
  }>,
  inventoryLimits?: MemberInventoryLimitsMap | null,
  applyLimits = true
): { lines: InventoryCostLineBreakdown[]; inventoryCost: number } => {
  const remainingLimits: MemberInventoryLimitsMap = {};
  if (applyLimits && inventoryLimits) {
    Object.entries(inventoryLimits).forEach(([inventoryId, qty]) => {
      if (Number.isFinite(qty) && qty >= 0) {
        remainingLimits[inventoryId] = qty;
      }
    });
  }

  const breakdown: InventoryCostLineBreakdown[] = lines.map((line) => {
    const unitPrice = line.unitPrice ?? 0;
    const totalQuantity = Math.max(0, line.totalQuantity || 0);
    let includedQty = 0;
    let billableQty = totalQuantity;

    if (applyLimits && line.inventoryId && line.inventoryId in remainingLimits) {
      includedQty = Math.min(totalQuantity, remainingLimits[line.inventoryId]);
      billableQty = totalQuantity - includedQty;
      remainingLimits[line.inventoryId] -= includedQty;
    }

    return {
      inventoryId: line.inventoryId,
      includedQty,
      billableQty,
      lineCost: billableQty * unitPrice,
      remainingAfterThis: line.inventoryId in remainingLimits
        ? remainingLimits[line.inventoryId]
        : 0,
      unitPrice,
      totalQuantity,
    };
  });

  return {
    lines: breakdown,
    inventoryCost: breakdown.reduce((sum, line) => sum + line.lineCost, 0),
  };
}

/**
 * Whether membership legacy single-resource fields (`plan_id` / `member_balance`)
 * should take precedence over per-resource JSON maps.
 * Only dual / membership plans use those legacy fields first.
 */
export const shouldPreferLegacyMemberPlanFields = (
  planType?: PlanType | null
): boolean => planType === 'dual' || planType === 'membership'

/**
 * Resolves the plan type of the member's own plan (`member.plan_id`) from the available plans.
 * Returns undefined when the member has no `plan_id` or the plan isn't in the list.
 */
export const getMemberPlanType = (
  member: MembershipSchema | null | undefined,
  plans?: (PlanSchema | ResourcePlanSchema)[] | null
): PlanType | undefined => {
  if (!member?.plan_id || !plans?.length) {
    return undefined;
  }
  return plans.find((availablePlan) => availablePlan?.plan_id === member.plan_id)?.plan_type;
}

/**
 * Resolves the member's booking plan id for a resource.
 * The decision is based on the plan type of the member's *own* plan (`member.plan_id`),
 * never the plan currently being rendered — otherwise a dual/membership plan elsewhere in
 * the list would make both `plan_id` and `booking_plan_ids` resolve as current plans.
 * Dual/membership members use `plan_id`; everyone else uses `booking_plan_ids` only.
 */
export const getMemberBookingPlanIdForResource = (
  member: MembershipSchema | null | undefined,
  resourceId: string,
  plans?: (PlanSchema | ResourcePlanSchema)[] | null
): string => {
  const memberPlanType = getMemberPlanType(member, plans);
  if (shouldPreferLegacyMemberPlanFields(memberPlanType) && member?.plan_id) {
    return member.plan_id;
  }
  const planIds = parseMemberResourceBookingPlanIds(member?.booking_plan_ids);
  return planIds[resourceId] ?? '';
}

/**
 * Resolves the member's booking balance for a resource.
 * For dual/membership plans, prefers legacy `member_balance` when truthy; otherwise uses `booking_balances` JSON map.
 */
export const getMemberBookingBalanceForResource = (
  member: MembershipSchema | null | undefined,
  resourceId: string,
  planType?: PlanType | null,
  currentBookingBalance?: number | null
): number => {
  if (currentBookingBalance !== null && currentBookingBalance !== undefined) {
    return currentBookingBalance;
  }
  if (shouldPreferLegacyMemberPlanFields(planType) && member?.member_balance) {
    return member.member_balance;
  }
  const balances = parseMemberResourceBookingBalances(member?.booking_balances);
  return balances[resourceId] ?? 0;
}

/**
 * Whether this is treated as a first booking against `new_plan_id` for the resource
 * (no prior plan, different plan, or zero remaining balance).
 * Resolves the member's current plan / balance through the shared resolvers so
 * `plan_id` / `member_balance` are only preferred for dual/membership members.
 */
export const isFirstBookingWithPlan = (
  username: string,
  member: MembershipSchema | null | undefined,
  resource_id: string,
  new_plan_id: string,
  current_booking_balance?: number | null,
  plans?: (PlanSchema | ResourcePlanSchema)[] | null
) => {
  const member_plan_type = getMemberPlanType(member, plans);
  const current_plan_id = getMemberBookingPlanIdForResource(member, resource_id, plans);

  // `|| null` keeps an unrecorded balance distinct from an explicitly passed 0
  const booking_balance: number | null = (current_booking_balance === null || current_booking_balance === undefined)
    ? (getMemberBookingBalanceForResource(member, resource_id, member_plan_type) || null)
    : current_booking_balance;

  return !current_plan_id || current_plan_id !== new_plan_id || booking_balance === 0;
}

/**
 * Reads one resource entry from a JSON-stringified member resource map.
 * Overloads keep plan-id (string) vs balance (number) return types consistent.
 */
export const extractMemberResourceMapFromJsonString = ((
  jsonString: string,
  resourceId: string,
  defaultValue: string | number
): string | number => {
  if (typeof defaultValue === 'string') {
    const parsed = parseMemberResourceBookingPlanIds(jsonString);
    return parsed[resourceId] ?? defaultValue;
  }
  const parsed = parseMemberResourceBookingBalances(jsonString);
  return parsed[resourceId] ?? defaultValue;
}) as {
  (
    jsonString: MemberResourceBookingPlanIdsJsonString | string,
    resourceId: string,
    defaultValue: string
  ): string
  (
    jsonString: MemberResourceBookingBalanceJsonString | string,
    resourceId: string,
    defaultValue: number
  ): number
}

/**
 * Updates one resource entry on a member resource map and returns the JSON string form.
 * Overloads keep plan-id vs balance value/map types consistent.
 */
export const convertMemberResourceMapToJsonString = ((
  memberResourceMap: MemberResourceBookingPlanIdsMap | MemberResourceBookingBalanceMap,
  resourceId: string,
  value: string | number
): MemberResourceBookingPlanIdsJsonString | MemberResourceBookingBalanceJsonString => {
  try {
    const updatedMap = { ...memberResourceMap, [resourceId]: value };
    if (typeof value === 'string') {
      return stringifyMemberResourceBookingPlanIds(updatedMap as MemberResourceBookingPlanIdsMap);
    }
    return stringifyMemberResourceBookingBalances(updatedMap as MemberResourceBookingBalanceMap);
  } catch (error) {
    console.error('Error converting MemberResourceMap to JSON:', error);
    return typeof value === 'string'
      ? EMPTY_MEMBER_BOOKING_PLAN_IDS_JSON
      : EMPTY_MEMBER_BOOKING_BALANCES_JSON;
  }
}) as {
  (
    memberResourceMap: MemberResourceBookingPlanIdsMap,
    resourceId: string,
    value: string
  ): MemberResourceBookingPlanIdsJsonString
  (
    memberResourceMap: MemberResourceBookingBalanceMap,
    resourceId: string,
    value: number
  ): MemberResourceBookingBalanceJsonString
}



export const formatMaxHours = (plan: ResourcePlanSchema): string => {

  const maxHours = (["weekly", "monthly", "annual"].includes(plan.plan_recurrence as any)) 
    ? Math.floor((plan.plan_max_hours || 0) / 24) 
    : (plan.plan_max_hours || 0)
  return maxHours ? `${maxHours} ${["weekly", "monthly", "annual"].includes(plan.plan_recurrence as any) ? "Days" : "Hours"}` : '';
};


export const extractPlanFields = <T extends PlanSchema | ResourcePlanSchema>(member: MembershipSchema | ResourceBookingSchema): T => {
  if (!member) return {} as T;
  
  return Object.entries(member)
    .filter(([key]) => key in (member as T))
    .reduce((acc, [key, value]) => ({
      ...acc,
      [key]: value
    }), {} as T);
};

export const getPeriodStyles = (periodType: TimeSlot['period_type']) => {
  switch (periodType) {
    case 'STANDARD':
      return {
        bg: 'bg-[#EDF7FF]',
        border: 'border-[#BBD7FF]',
        text: 'text-[#0B5FFF]',
        dot: 'bg-[#0B5FFF]'
      };
    case 'PEAK':
      return {
        bg: 'bg-[#FFF1E6]',
        border: 'border-[#FFC9A6]',
        text: 'text-[#D9480F]',
        dot: 'bg-[#D9480F]'
      };
    case 'OFF_PEAK':
      return {
        bg: 'bg-emerald-50/70',
        border: 'border-emerald-200',
        text: 'text-emerald-400',
        dot: 'bg-emerald-400'
      };
    default:
      return {
        bg: 'bg-[#EDF7FF]',
        border: 'border-[#BBD7FF]',
        text: 'text-[#0B5FFF]',
        dot: 'bg-[#0B5FFF]'
      };
  }
};


export const formatIDPUser = (user: User): IdpAccountRequestSchema => {
  const idpUser: IdpAccountRequestSchema = {
      uid: user.uid,
      emailVerified: user.emailVerified,
      displayName: user.displayName || '',
      isAnonymous: user.isAnonymous,
      photoURL: user.photoURL || '',
      // providerData: user.providerData as IdpProviderData[],
      // stsTokenManager: user.metadata.lastLoginAt || '',
      createdAt: user.metadata.creationTime || '',
      // lastLoginAt: user.metadata.lastLoginAt || '',
      providerId: user?.providerData?.[0]?.providerId || '',
      // apiKey: user.apiKey || '',
      // appName: user.appName || ''
  }

  // console.log("user.providerData", user.providerData)

  if (user.email){
    idpUser.email = user.email;
  }
  // if (user.providerData && user.providerData.length > 0){
  //   // idpUser.providerData = user.providerData as IdpProviderData[];
  // }
  return idpUser;
}


export const buildPrompt = (businessDetails: MyBusinessSchema) => {
  if (!businessDetails || !businessDetails.business_name || !businessDetails.business_category || !businessDetails.business_type || !businessDetails.cooking_state || !businessDetails.cooking_county || !businessDetails.kitchen_type || !businessDetails.business_stage || !businessDetails.max_chefs) {
    return ''
  }
  return `
  As a food business consultant, please provide detailed guidance on licensing and compliance requirements for the following food business:

  Business Details:
  - Name: ${businessDetails.business_name}
  - Category: ${businessDetails.business_category}
  - Type: ${businessDetails.business_type}
  - Location: ${businessDetails.cooking_state}, ${businessDetails.cooking_county}
  - Kitchen Type: ${businessDetails.kitchen_type}
  - Business Stage: ${businessDetails.business_stage}
  - Maximum Chefs: ${businessDetails.max_chefs}

  Please provide:
  1. Required licenses and permits for this specific food business type and location
  2. Health and safety compliance requirements
  3. Food handling and storage regulations
  4. Kitchen facility requirements based on the specified equipment and features
  5. Documentation needed for regulatory compliance
  6. Timeline estimates for obtaining necessary permits
  7. Estimated costs for licenses and compliance measures
  8. Any other relevant information that would be helpful for the business owner to know

  Focus on requirements specific to ${businessDetails.cooking_state} and ${businessDetails.cooking_county} jurisdictions.
  `
}


export const getActionButtonLabel = (resourceType: ResourceType, resource?: ResourceSchema | null) => {
  var actionButtonLabel = "Book"
  switch(resourceType){
    case 'kitchen':
      actionButtonLabel = 'Book'
      break
    case 'documentation':
      actionButtonLabel = 'Obtain'
      break
    case 'training':
      actionButtonLabel = 'Register'
      break
    case 'funding':
      actionButtonLabel = usesReferralHandoff(resource) ? 'Get Started' : 'Apply'
      break
    case 'expert':
      actionButtonLabel = isContactBuilderResource(resource) ? 'Build list' : 'Schedule'
      break
    case 'farm':
      actionButtonLabel = 'Place Order'
      break
    default:
      actionButtonLabel = 'Book'
      break
  }
  return actionButtonLabel
}

const CLOCK_TIME_PARSE_FORMATS = ['hh:mm A', 'h:mm A', 'HH:mm', 'H:mm'] as const;

/** Parse a clock time string (e.g. "09:00 AM") onto a specific calendar day. */
export const parseClockTimeOnDate = (
  time: string,
  baseDate: moment.Moment
): moment.Moment | null => {
  const parsed = moment(time.trim(), [...CLOCK_TIME_PARSE_FORMATS]);
  if (!parsed.isValid()) return null;
  return moment(baseDate).set({
    hour: parsed.hour(),
    minute: parsed.minute(),
    second: 0,
    millisecond: 0,
  });
};

export const getResourceBufferMinutes = (resource?: ResourceSchema): number =>
  resource?.resource_options?.floor_plan?.buffer_time ?? 0;

export const calculateAvailableTimeBlocks = (
  openTime: string,
  closeTime: string,
  requestedHours: number,
  bookings: Array<ResourceBookingSchema>,
  baseDate: moment.Moment, // date to anchor the time blocks
  bufferMinutes = 0
): TimeBlock[] => {
  const blocks: TimeBlock[] = [];

  const startMoment = parseClockTimeOnDate(openTime, baseDate);
  const endMoment = parseClockTimeOnDate(closeTime, baseDate);

  if (!startMoment || !endMoment) {
    return blocks;
  }

  // Handle overnight ranges (e.g., 20:00 - 02:00 next day)
  if (endMoment.isSameOrBefore(startMoment)) {
    endMoment.add(1, "day");
  }

  let currentTime = startMoment.clone();
  let iterations = 0;
  const maxIterations = 1000; // Prevent infinite loops

  while (
    currentTime.clone().add(requestedHours, "hours").isSameOrBefore(endMoment) &&
    iterations < maxIterations
  ) {
    const blockEnd = currentTime.clone().add(requestedHours, "hours");
    let isAvailable = true;

    for (const booking of bookings) {
      const bookingStart = moment(booking.start_time);
      const bookingEnd = moment(booking.end_time);
      const blockedEnd =
        bufferMinutes > 0
          ? bookingEnd.clone().add(bufferMinutes, 'minutes')
          : bookingEnd;

      if (currentTime.isBefore(blockedEnd) && blockEnd.isAfter(bookingStart)) {
        isAvailable = false;
        break;
      }
    }

    if (isAvailable) {
      blocks.push({
        start: currentTime.clone(),
        end: blockEnd.clone(),
      });
    }

    currentTime.add(30, "minutes");
    iterations++;
  }

  return blocks;
};


export const calculateAvailableTimeBlocksForSlots = (
  timeSlots: TimeSlot[],
  requestedHours: number,
  bookings: Array<ResourceBookingSchema>,
  baseDate: moment.Moment,
  bufferMinutes = 0
): TimeBlock[] => {
  if (!timeSlots || timeSlots.length === 0) return [];

  const allBlocks: TimeBlock[] = [];
  timeSlots.forEach((slot) => {
    if (slot && slot.open_time && slot.close_time) {
      const blocks = calculateAvailableTimeBlocks(
        slot.open_time,
        slot.close_time,
        requestedHours,
        bookings,
        baseDate,
        bufferMinutes
      );
      allBlocks.push(...blocks);
    }
  });

  // Sort blocks by start time to keep chronological order
  allBlocks.sort((a, b) => a.start.diff(b.start));
  return allBlocks;
};


export const areDatesInSameMonth = (date1: Date, date2: Date) => {
  // Extract year and month components from both dates
  const year1 = date1.getFullYear();
  const month1 = date1.getMonth();

  const year2 = date2.getFullYear();
  const month2 = date2.getMonth();

  // Compare the year and month components
  return year1 === year2 && month1 === month2;
};


export const findBookingsForDay = (day: moment.Moment, bookings: ResourceBookingSchema[]): Array<ResourceBookingSchema> => {
  const bookingsForDay: Array<ResourceBookingSchema> = [];
  if (bookings && bookings.length > 0) {
    for (const booking of bookings) {
      const startTime = moment(booking.start_time);
      const endTime = moment(booking.end_time);

      if (
        startTime.isSameOrBefore(day, "day") &&
        endTime.isSameOrAfter(day, "day")
      ) {
        bookingsForDay.push(booking);
      }
    }
  }
  return bookingsForDay;
};


export const areBookingsLessThan12Hours = (day: Date, bookings: ResourceBookingSchema[]) => {
  // const startOfDay = moment(day).startOf("day");
  // const endOfDay = moment(day).endOf("day");
  let totalTime = 0; 
  const bookingsInDay = findBookingsForDay(moment(day), bookings);
  for (const booking of bookingsInDay) {
    totalTime += moment(booking.end_time).diff(booking.start_time);
  }
  const test = totalTime < 12 * 60 * 60 * 1000;

  return test;
};


export const isTimeSlot = (value: TimeSlot): boolean => {
  return !!(
    value && typeof value === 'object' &&
    'open_time' in value && value.open_time &&
    'close_time' in value && value.close_time
  );
};


/** True when the kitchen is fully closed on this calendar day (closed_dates or dated closed_hours). */
export const isKitchenClosedOnDate = (
  day: Date,
  resource: ResourceSchema
): boolean => {
  const options = resource?.resource_options;
  if (!options) {
    return false;
  }

  const dayStr = moment(day).format('YYYY-MM-DD');
  const closedDatesList = options.closed_dates as ClosedDatesType[] | undefined;
  if (closedDatesList?.length) {
    for (const block of closedDatesList) {
      if (
        block.dates?.some(
          (d) => moment(d).format('YYYY-MM-DD') === dayStr
        )
      ) {
        return true;
      }
    }
  }

  const closedHours = options.closed_hours as Array<{ date?: string }> | undefined;
  if (closedHours?.length) {
    for (const entry of closedHours) {
      if (entry.date && moment(entry.date).format('YYYY-MM-DD') === dayStr) {
        return true;
      }
    }
  }

  return false;
};

export const dayAvailability = (
  day: Date,
  currentResource: ResourceSchema,
  nHours: number,
  bookings: ResourceBookingSchema[]
) => {
  if (isKitchenClosedOnDate(day, currentResource)) {
    return false;
  }

  const dayOfWeek = moment(day).format("ddd").toLowerCase();
  const dayOptions = currentResource?.resource_options?.[dayOfWeek] as TimeSlot[];
  if (!dayOptions || !(Array.isArray(dayOptions)) || dayOptions.length === 0) {
    return false;
  }

  const bufferMinutes = getResourceBufferMinutes(currentResource);
  const availableBlocks = calculateAvailableTimeBlocksForSlots(
    dayOptions,
    nHours,
    findBookingsForDay(moment(day), bookings || []),
    moment(day),
    bufferMinutes
  );
  return availableBlocks.length > 0;
};

const timeBlocksMatchByClock = (a: TimeBlock, b: TimeBlock): boolean =>
  a.start.format('HH:mm') === b.start.format('HH:mm') &&
  a.end.format('HH:mm') === b.end.format('HH:mm');

export type MultiDateTimeBlockIssues = {
  closedDates: string[];
  unavailableDates: string[];
};

/** For each date, detect closed days or missing matching slot for the selected time range. */
export const getDatesWithTimeBlockIssues = (
  dates: Date[],
  nHours: number,
  resource: ResourceSchema,
  bookings: ResourceBookingSchema[],
  selectedBlock: TimeBlock
): MultiDateTimeBlockIssues => {
  const closedDates: string[] = [];
  const unavailableDates: string[] = [];

  if (!dates.length || nHours <= 0 || !selectedBlock) {
    return { closedDates, unavailableDates };
  }

  for (const date of dates) {
    const dateStr = moment(date).format('YYYY-MM-DD');
    const dayOfWeek = moment(date).format('ddd').toLowerCase();
    const dayOptions = resource?.resource_options?.[dayOfWeek] as TimeSlot[];

    if (!dayOptions || !Array.isArray(dayOptions) || dayOptions.length === 0) {
      closedDates.push(dateStr);
      continue;
    }

    if (!dayAvailability(date, resource, nHours, bookings)) {
      closedDates.push(dateStr);
      continue;
    }

    const bufferMinutes = getResourceBufferMinutes(resource);
    const blocks = calculateAvailableTimeBlocksForSlots(
      dayOptions,
      nHours,
      findBookingsForDay(moment(date), bookings || []),
      moment(date),
      bufferMinutes
    );

    if (!blocks.some((block) => timeBlocksMatchByClock(block, selectedBlock))) {
      unavailableDates.push(dateStr);
    }
  }

  return { closedDates, unavailableDates };
};

export const formatMultiDateTimeBlockError = (
  issues: MultiDateTimeBlockIssues,
  selectedBlock?: TimeBlock
): string | null => {
  const { closedDates, unavailableDates } = issues;
  if (!closedDates.length && !unavailableDates.length) {
    return null;
  }

  const parts: string[] = [];
  const timeRange = selectedBlock
    ? `${selectedBlock.start.format('h:mm A')} – ${selectedBlock.end.format('h:mm A')}`
    : 'This time';

  if (closedDates.length) {
    const labels = closedDates.map((d) => moment(d, 'YYYY-MM-DD').format('ddd, MMM D'));
    parts.push(
      closedDates.length === 1
        ? `The kitchen is closed or has no availability on ${labels[0]}`
        : `The kitchen is closed or has no availability on: ${labels.join(', ')}`
    );
  }

  if (unavailableDates.length) {
    const labels = unavailableDates.map((d) => moment(d, 'YYYY-MM-DD').format('ddd, MMM D'));
    parts.push(
      unavailableDates.length === 1
        ? `${timeRange} is not available on ${labels[0]}`
        : `${timeRange} is not available on: ${labels.join(', ')}`
    );
  }

  return `${parts.join('. ')}. Please choose a different time or adjust your dates.`;
};

export const formatDateString = (monthString?: number, yearString?: number): string => {
  if (monthString && yearString && yearString.toString().length === 4){
    return `${(monthString.toString().length < 2) ? `0${monthString}` : monthString}/${yearString.toString().slice(-2)}`
  } else if (yearString && yearString.toString().length === 4) {
    return yearString.toString().slice(-2);
  } else if (monthString && monthString.toString().length === 1){
    return `0${monthString}`
  }
  return ''
}


export const filterItemsByCategoryAndName = (data: FilterOptionSchema[], category: string, itemName: string): string[] => {
  const categoryData = data.find(cat => cat.category.toLowerCase() === category.toLowerCase());
  if (!categoryData) {
    return [];
  }
  return categoryData.items.filter(item => item.toLowerCase().includes(itemName.toLowerCase()));
}

export const searchInventoryCatalog = (category: string, query: string): string[] => {
  const inventoryItems: FilterOptionSchema[] = inventoryCatalog as FilterOptionSchema[];
  return filterItemsByCategoryAndName(inventoryItems, category, query)
}

export const getItemsByCategory = (data: FilterOptionSchema[], category: string): string[] => {
  const categoryData = data.find(cat => cat.category.toLowerCase() === category.toLowerCase());
  if (!categoryData) {
    return [];
  }
  return categoryData.items
}

export const listInventoriesByCategory = (category: string): string[] => {
  const inventoryItems: FilterOptionSchema[] = inventoryCatalog as FilterOptionSchema[];
  return getItemsByCategory(inventoryItems, category)
}

export const requirementCodes: string[] = [
  "FMP",
  "MFRST",
  "CLNRY",
  "PSTRT",
  "SSA",
  "SSALRGNS",
  "SSFHNDLR",
  "SSPRC",
  "SSI",
  "PIC Ohio",
  "SSWP",
  "SSMGR",
  "SMMR",
  "TXFG",
  "ID",
  "PID",
  "GLI",
  "HACCP",
];


// export const fetchRequirementsMet = (communityRequirements: RequirementSchema[], memberCredentials: CredentialSchema[], paymentMethods: any[]): CurrentRequirementsSchema[] => {

//   console.log("communityRequirements", communityRequirements)
//   console.log("memberCredentials", memberCredentials)
//   console.log("paymentMethods", paymentMethods)
//   return [
//       {
//         code: communityRequirements[0].code,
//         name: communityRequirements[0].name,
//         description: communityRequirements[0].description || "",
//         isMet: memberCredentials.some((item) => (item["category"] === communityRequirements[0].name && item['is_valid'])),
//         isRequired: false
//       }
//   ]
// }


// export const fetchRequirementsLookUp = (memberCredentials: CredentialSchema[], paymentMethods: any[]): CurrentRequirementsSchema[] => {
//   return [
//       {
//         code: "FMP",
//         name: "Foodservice Management Professional Certification",
//         description: "",
//         is_met: memberCredentials.some((item) => (item["category"] === "FMP" && item['is_valid'])),
//         is_required: false
//       },
//       { 
//         code: "MFRST", 
//         name: "ManageFirst", 
//         description: "", 
//         is_met: memberCredentials.some((item) => (item["category"] === "MFRST" && item['is_valid'])) ,
//         is_required: false
//       },
//       {
//         code: "CLNRY",
//         name: "National Restaurant Association Culinary Exams",
//         description: "",
//         is_met: memberCredentials.some((item) => (item["category"] === "CLNRY" && item['is_valid'])),
//         is_required: false
//       },
//       { 
//         code: "PSTRT", 
//         name: "ProStart",  
//         description: "",
//         is_met: memberCredentials.some((item) => (item["category"] === "PSTRT" && item['is_valid'])),
//         is_required: false
//       },
//       { 
//         code: "SSA", 
//         name: "ServSafe Alcohol", 
//         description: "", 
//         is_met: memberCredentials.some((item) => (item["category"] === "SSA" && item['is_valid'])),
//         is_required: false
//       },
//       {
//         code: "SSALRGNS",
//         name: "ServSafe Allergens Online Course", 
//         description: "", 
//         is_met: memberCredentials.some((item) => (item["category"] === "SSALRGNS" && item['is_valid'])),
//         is_required: false
//       },
//       { 
//         code: "SSFHNDLR", 
//         name: "ServSafe Food Handler", 
//         description: "", 
//         is_met: memberCredentials.some((item) => (item["category"] === "SSFHNDLR" && item['is_valid'])),
//         is_required: false
//       },
//       {
//         code: "SSPRC",
//         name: "ServSafe Instructor and Proctor Certificates", 
//         description: "", 
//         is_met: memberCredentials.some((item) => (item["category"] === "SSPRC" && item['is_valid'])),
//         is_required: false
//       },
//       {
//         code: "SSI",
//         name: "ServSafe International Food Safety", 
//         description: "", 
//         is_met: memberCredentials.some((item) => (item["category"] === "SSI" && item['is_valid'])),
//         is_required: false
//       },
//       {
//         code: "PIC Ohio",
//         name: "ServSafe Person in Charge – Ohio",
//         description: "", 
//         is_met: memberCredentials.some((item) => (item["category"] === "PIC Ohio" && item['is_valid'])),
//         is_required: false
//       },
//       { 
//         code: "SSWP", 
//         name: "ServSafe Workplace", 
//         description: "", 
//         is_met: memberCredentials.some((item) => (item["category"] === "SSWP" && item['is_valid'])),
//         is_required: false
//       },
//       {
//         code: "SSMGR",
//         name: "ServSafe: Food Protection Manager Certification", 
//         description: "", 
//         is_met: memberCredentials.some((item) => (item["category"] === "SSMGR" && item['is_valid'])),
//         is_required: false
//       },
//       { 
//         code: "SMMR", 
//         name: "Summer Institutes", 
//         description: "", 
//         is_met: memberCredentials.some((item) => (item["category"] === "SMMR" && item['is_valid'])),
//         is_required: false
//       },
//       { 
//         code: "TXFG", 
//         name: "Texas FoodGuard", 
//         description: "", 
//         is_met: memberCredentials.some((item) => (item["category"] === "TXFG" && item['is_valid'])), 
//         is_required: false
//       },
//       {
//         code: "GLI", 
//         name: "General Liability Insurance", 
//         description: "", 
//         is_met: memberCredentials.some((item) => (item["category"] === "GLI" && item['is_valid'])), 
//         is_required: false
//       },
//       { 
//         code: "HACCP", 
//         name: "HACCP Certificate", 
//         description: "", 
//         is_met: memberCredentials.some((item) => (item["category"] === "HACCP" && item['is_valid'])), 
//         is_required: false
//       },
//       { 
//         code: "ID", 
//         name: "Government ID", 
//         description: "Upload a government ID with your photo to verify your identity.", 
//         is_met: memberCredentials.some((item) => (item["category"] === "ID" && item['is_valid'])), 
//         is_required: false  
//       },
//       { 
//         code: "PID", 
//         name: "Photo ID", 
//         description: "Upload a legal document with your photo to verify your identity.", 
//         is_met: memberCredentials.some((item) => (item["category"] === "PID" && item['is_valid'])),
//         is_required: true
//       },
//       { 
//         code: "PAY",
//         name: "Add your payment method", 
//         description: "Add your preferred payment method for easy transactions.", 
//         is_met: (paymentMethods && paymentMethods.length > 0) ? true : false, 
//         is_required: true
//       }
//   ]
// }

// export const getDirectLinkFromGoogleDriveSharedLink = (sharedLink: string | undefined): string => {
//   // Regular expression to match Google Drive shared links
//   const regex = /https:\/\/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)\/view\?usp=sharing/i;
//   if (sharedLink){
//     const match = regex.exec(sharedLink);
//     if (!match) {
//       return ""; // Not a valid Google Drive shared link
//     }
//     const fileId = match[1]; // Extract the file ID from the match
//     // console.log("fileId", fileId)
//     // Potential direct link format (replace with your actual generation logic)
//     // You might need to use a Google Drive API or a third-party service to obtain the actual direct link based on the file ID.
//     // https://drive.usercontent.google.com/download?id=1EqddZcRTk9btkxAhWu3Y7osd0KrFdsYk&export=view&authuser=0
//     // const potentialDirectLink = `https://drive.google.com/uc?export=view&id=${fileId}`;
//     // https://drive.usercontent.google.com/download?id=1EqddZcRTk9btkxAhWu3Y7osd0KrFdsYk&export=view&authuser=0
//     // https://drive.usercontent.google.com/download?id=1EqddZcRTk9btkxAhWu3Y7osd0KrFdsYk&export=view
//     const potentialDirectLink = `https://drive.usercontent.google.com/download?id=${fileId}&export=view`;
  
//     return potentialDirectLink;
//   }
//   return ""
// }

export const handleGroupMessages = (allMessages: MessageSchema[], groupByField: string): GroupedMessageListType => {
  const groupMessages = allMessages.reduce((result: GroupedMessageListType, message: MessageSchema) => {
      const groupByValue = message[groupByField as keyof MessageSchema] as string;
      if (!result[groupByValue]) {
          result[groupByValue] = [];
      }
      result[groupByValue].push(message);
      return result;
  }, {});
  return groupMessages
}

export const getDirectLinkFromGoogleDriveSharedLink = (link: string | undefined): string => {
  // Regular expressions to match S3 and Google Drive shared links
  // const s3Regex = /^https:\/w\/s3\.amazonaws\.com\/.+\/.+$/;
  const googleDriveRegex = /^https:\/\/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)\/view\?usp=sharing$/;
  // console.log("link", link)
  if (link && link.includes('s3')) {
    // If it matches the S3 regex, return the link as is
    // console.log("s3 link", link)
    return link;
  } else if (link && googleDriveRegex.test(link)) {
    // If it matches the Google Drive regex, convert to direct link format
    const match = link.match(googleDriveRegex);
    if (match) {
      const fileId = match[1];
      return `https://drive.google.com/uc?export=view&id=${fileId}`;
    }
  }

  

  // If neither regex matches, return an empty string
  return '';
}

const getDayName = (dateString: string): string => {
  // Parse the date string
  const date = moment(dateString, 'YYYY-MM-DD');
  // Get the day name
  return date.format('dddd');
}


const getDayOfMonth = (dateString: string): number  => {
  // Parse the date string
  const date = moment(dateString, 'YYYY-MM-DD');
  // Get the day of the month
  return date.date();
}


const getDayOfMonthWithSuffix = (dateString: string): string => {
  // Parse the date string
  const date = moment(dateString, 'YYYY-MM-DD');
  // Get the day of the month with ordinal suffix
  return date.format('Do');
}


const addDaysToToday = (days: number): string => {
  // Get today's date and add the specified number of days
  const newDate = moment().add(days, 'days');
  // Format the new date as YYYY-MM-DD
  return newDate.format('YYYY-MM-DD');
}

const calculateDateDifference = (fromDate: string, toDate: string, booking_recurrence: string) => {
  var nOccurrences = 0
  if (fromDate && toDate){
    // Parse the dates
    const start = moment(fromDate, 'YYYY-MM-DD');
    const end = moment(toDate, 'YYYY-MM-DD');
    switch(booking_recurrence){
      case 'daily':
        nOccurrences = end.diff(start, 'days') + 1;
        return nOccurrences
      case 'weekly':
        nOccurrences = end.diff(start, 'weeks');
        let startDay = getDayOfMonth(fromDate)
        let endDay = getDayOfMonth(toDate)
        if (endDay >= startDay){
          nOccurrences += 1
        }
        return nOccurrences
      case 'monthly':
        nOccurrences = end.diff(start, 'months') + 1;
        return nOccurrences
      case 'annual':
        nOccurrences = end.diff(start, 'years') + 1;
        return nOccurrences
      default:
        return 1
    }
  }
  return nOccurrences
}

/** Booking recurrence options shown in kitchen date picker (excludes hourly/daily plan types). */
export const BOOKING_RECURRENCE_OPTIONS: { value: BookingRecurrenceType; label: string }[] = [
  { value: 'once', label: 'Does not repeat' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'annual', label: 'Annually' },
];

export type ExpandRecurrenceOptions = {
  resource?: ResourceSchema;
  nHours?: number;
  bookings?: ResourceBookingSchema[];
};

const isSelectableRecurrenceDate = (
  day: Date,
  options?: ExpandRecurrenceOptions
): boolean => {
  if (!options?.resource) {
    return true;
  }
  if (isKitchenClosedOnDate(day, options.resource)) {
    return false;
  }
  const hours = options.nHours ?? 0;
  if (hours > 0 && !dayAvailability(day, options.resource, hours, options.bookings ?? [])) {
    return false;
  }
  return true;
};

const calendarDayUtc = (date: Date): number =>
  Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());

const daysBetweenCalendarDates = (later: Date, earlier: Date): number =>
  Math.round((calendarDayUtc(later) - calendarDayUtc(earlier)) / (24 * 60 * 60 * 1000));

/** Infer recurrence type from a sorted set of selected dates (returns once if no pattern). */
export const inferRecurrenceFromDates = (dates: Date[]): BookingRecurrenceType => {
  if (dates.length <= 1) {
    return 'once';
  }

  const sorted = [...dates].sort((a, b) => calendarDayUtc(a) - calendarDayUtc(b));

  const isDaily = sorted.every((date, index) => {
    if (index === 0) return true;
    return daysBetweenCalendarDates(date, sorted[index - 1]) === 1;
  });
  if (isDaily) {
    return 'daily';
  }

  const isWeekly = sorted.every((date, index) => {
    if (index === 0) return true;
    return daysBetweenCalendarDates(date, sorted[index - 1]) === 7;
  });
  if (isWeekly) {
    return 'weekly';
  }

  const isMonthly = sorted.every((date, index) => {
    if (index === 0) return true;
    const prev = moment(sorted[index - 1]);
    const expected = prev.clone().add(1, 'month');
    return moment(date).isSame(expected, 'day');
  });
  if (isMonthly) {
    return 'monthly';
  }

  const isAnnual = sorted.every((date, index) => {
    if (index === 0) return true;
    const prev = moment(sorted[index - 1]);
    const expected = prev.clone().add(1, 'year');
    return moment(date).isSame(expected, 'day');
  });
  if (isAnnual) {
    return 'annual';
  }

  return 'once';
};

/** True when sorted highlighted days exactly match the recurrence cadence (daily/weekly/monthly/annual). */
export const selectedDaysMatchRecurrence = (
  dates: Date[],
  recurrence: BookingRecurrenceType
): boolean => {
  if (dates.length <= 1 || recurrence === 'once') {
    return recurrence === 'once';
  }
  return inferRecurrenceFromDates([...dates].sort((a, b) => calendarDayUtc(a) - calendarDayUtc(b))) === recurrence;
};

export type RecurrenceControlsFromDates = {
  recurrence: BookingRecurrenceType;
  /** Last date in the series when recurring; null for custom / does-not-repeat selection. */
  endRecurrence: string | null;
};

/** Map calendar selection to Repeat booking + Repeat until controls. */
export const getRecurrenceControlsFromSelectedDays = (
  dates: Date[]
): RecurrenceControlsFromDates => {
  if (dates.length === 0) {
    return { recurrence: 'once', endRecurrence: null };
  }

  const sorted = [...dates].sort((a, b) => calendarDayUtc(a) - calendarDayUtc(b));
  const inferred = inferRecurrenceFromDates(sorted);

  if (inferred === 'once') {
    return { recurrence: 'once', endRecurrence: null };
  }

  return {
    recurrence: inferred,
    endRecurrence: moment(sorted[sorted.length - 1]).format('YYYY-MM-DD'),
  };
};

/**
 * Recurrence APIs can treat end dates as exclusive; when the selected last day equals
 * endRecurrence, bump by one day so the last highlighted day is included.
 */
export const getSubmissionEndRecurrence = (
  endRecurrence: string,
  selectedDays: Date[]
): string => {
  if (!endRecurrence || selectedDays.length === 0) {
    return endRecurrence;
  }

  const sorted = [...selectedDays].sort((a, b) => calendarDayUtc(a) - calendarDayUtc(b));
  const lastSelected = moment(sorted[sorted.length - 1]).format('YYYY-MM-DD');
  if (lastSelected !== endRecurrence) {
    return endRecurrence;
  }
  return moment(endRecurrence).add(1, 'day').format('YYYY-MM-DD');
};

/** Expand anchor date through end_recurrence for recurring kitchen bookings. */
export const expandRecurrenceDates = (
  startDate: Date | string,
  endRecurrence: string,
  recurrence: BookingRecurrenceType,
  options?: ExpandRecurrenceOptions
): Date[] => {
  if (!recurrence || recurrence === 'once' || !endRecurrence) {
    const start = moment(startDate).startOf('day');
    const single = start.isValid() ? [start.toDate()] : [];
    return single.filter((d) => isSelectableRecurrenceDate(d, options));
  }

  const start = moment(startDate).startOf('day');
  const end = moment(endRecurrence).startOf('day');
  if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
    return [];
  }

  const dates: Date[] = [];
  let cursor = start.clone();

  switch (recurrence) {
    case 'daily':
      while (cursor.isSameOrBefore(end)) {
        dates.push(cursor.toDate());
        cursor.add(1, 'day');
      }
      break;
    case 'weekly':
      while (cursor.isSameOrBefore(end)) {
        dates.push(cursor.toDate());
        cursor.add(1, 'week');
      }
      break;
    case 'monthly':
      while (cursor.isSameOrBefore(end)) {
        dates.push(cursor.toDate());
        cursor.add(1, 'month');
      }
      break;
    case 'annual':
      while (cursor.isSameOrBefore(end)) {
        dates.push(cursor.toDate());
        cursor.add(1, 'year');
      }
      break;
    default:
      dates.push(start.toDate());
  }

  return dates.filter((d) => isSelectableRecurrenceDate(d, options));
};

const maxHours = (capacity: number, recurrence: string | 'daily' | 'weekly' | 'monthly' | 'annual',  hours_left: number) => {
  if (recurrence && capacity){
    switch (recurrence){
      case 'daily':
        // 24 hrs
        hours_left = hours_left ? hours_left : Math.round(16 / capacity)
        return hours_left
      case 'weekly':
        // 168 hrs
        hours_left = hours_left ? hours_left : Math.round(115 / capacity)
        return hours_left
      case 'monthly':
        // 730 hrs
        hours_left = hours_left ? hours_left : Math.round(500 / capacity)
        return hours_left
      case 'yearly':
        // 8760 hrs
        hours_left = hours_left ? hours_left : Math.round(6000 / capacity)
        return hours_left
      default:
        return 0
    }
  }
  return 0
}


const objectToFormData = (obj: { [key: string]: any }): FormData =>  {
  const formData = new FormData();
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      formData.append(key, value);
    }
  }
  return formData;
}

 // Define a function to find member by attribute value
 const findMemberByAttribute = (objects: MembershipSchema[], attribute: keyof MembershipSchema, value: any) => {
  for (const obj of objects) {
      if (obj[attribute] === value) {
          return obj;
      }
  }
  return {};
}


 // Define a function to find an object by attribute value
 export const isResourceSaved = <T extends { [key: string]: any }>(resourceId: string, objects: T[]) => {
  for (const obj of objects) {
      if (!obj) continue;
      if (obj['resource_id'] === resourceId || obj['id'] === resourceId) {
        return true;
      }
  }
  return false;
}


 // Define a function to find an object by attribute value
 const findObjectByAttribute = <T>(objects: T[], attribute: keyof T, value: string) => {
  for (const obj of objects) {
      if (obj[attribute] === value) {
          return obj;
      }
  }
  return {};
}


export const planRecurrenceMap = {
  'hourly': 'hour',
  'daily': 'day',
  'weekly': 'week',
  'monthly': 'month',
  'annual': 'year'
}


const getMembershipStatusColor = (status: string): {bgColor: string, primaryTextColor: string, secondaryTextColor: string} => {
  if (status) {
    status = status.toLowerCase();
    switch (status) {
      case "approved":
        return {bgColor: "#E7F5EC", primaryTextColor: '#b9dc8d', secondaryTextColor: '#819a62'};
      case "active":
        return {bgColor: "#ebf5ec", primaryTextColor: '#399f48', secondaryTextColor: '#afd8b5'};
      case "verified":
        return {bgColor: "#fef5ea", primaryTextColor: '#F69E35', secondaryTextColor: '#fce1c2'};
      case "pending":
        return {bgColor: "#edf5f7", primaryTextColor: '#51a0b5', secondaryTextColor: '#a8cfda'};
      case "declined":
        return {bgColor: "#faeeee", primaryTextColor: '#CE5D5D', secondaryTextColor: '#e6aeae'};
      case "inactive":
        return {bgColor: "#eee", primaryTextColor: '#9a9a9a', secondaryTextColor: '#d6d6d6'};
      case "removed":
        return {bgColor: "#eee", primaryTextColor: '#668790', secondaryTextColor: '#696969'};
      default:
        return {bgColor: "#eee", primaryTextColor: '#bbb', secondaryTextColor: '#696969'};
    }
  }
  return {bgColor: "#eee", primaryTextColor: '#bbb', secondaryTextColor: '#696969'};
};

/**
 * Status colors for recurring subscription plans (weekly / monthly / annual).
 * Reuses membership palette for shared statuses and adds subscription-specific ones.
 */
const getSubscriptionStatusColor = (
  status: string
): { bgColor: string; primaryTextColor: string; secondaryTextColor: string } => {
  if (!status) {
    return { bgColor: '#eee', primaryTextColor: '#bbb', secondaryTextColor: '#696969' };
  }
  const normalized = status.toLowerCase();
  switch (normalized) {
    case 'past_due':
      return { bgColor: '#fef5ea', primaryTextColor: '#F69E35', secondaryTextColor: '#fce1c2' };
    case 'canceled':
    case 'cancelled':
      return { bgColor: '#faeeee', primaryTextColor: '#CE5D5D', secondaryTextColor: '#e6aeae' };
    case 'trialing':
      return { bgColor: '#edf5f7', primaryTextColor: '#51a0b5', secondaryTextColor: '#a8cfda' };
    default:
      return getMembershipStatusColor(normalized);
  }
};

/**
 * Formats an ISO date/time for subscription renewal / charge display.
 * Returns an em dash when the value is missing or invalid.
 */
export const formatSubscriptionDate = (isoDate?: string | null): string => {
  if (!isoDate) return '—';
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Human-readable label for subscription status (e.g. past_due → Past Due).
 */
export const formatSubscriptionStatusLabel = (status?: string | null): string => {
  if (!status) return 'Unknown';
  return status
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
};

/** True only when cancel_at_period_end is explicitly boolean true */
export const isSubscriptionCancelAtPeriodEnd = (value: unknown): boolean => value === true;

/**
 * Advances a date by one subscription billing period.
 */
export const addSubscriptionBillingPeriod = (
  from: Date,
  recurrence: SubscriptionRecurrenceType
): Date => {
  const next = new Date(from.getTime());
  switch (recurrence) {
    case 'weekly':
      next.setDate(next.getDate() + 7);
      break;
    case 'monthly':
      next.setMonth(next.getMonth() + 1);
      break;
    case 'annual':
      next.setFullYear(next.getFullYear() + 1);
      break;
    default:
      break;
  }
  return next;
};

type SubscriptionChargeDateFields = Pick<
  SubscriptionSchema,
  | 'next_charge_date'
  | 'current_period_end'
  | 'started'
  | 'plan_recurrence'
  | 'cancel_at_period_end'
>;

/**
 * Resolves the next-charge / access-through display date.
 * Prefers API charge/period dates; when those are missing—or when cancel_at_period_end
 * is invalid/undefined—falls back to calculating from `started` + plan recurrence.
 */
export const resolveSubscriptionChargeDate = (
  subscription: SubscriptionChargeDateFields
): string => {
  const cancelsAtEnd = isSubscriptionCancelAtPeriodEnd(subscription.cancel_at_period_end);
  const apiDate = cancelsAtEnd
    ? subscription.current_period_end || subscription.next_charge_date
    : subscription.next_charge_date || subscription.current_period_end;

  if (apiDate) {
    const formatted = formatSubscriptionDate(apiDate);
    if (formatted !== '—') return formatted;
  }

  if (!subscription.started) return '—';
  const started = new Date(subscription.started);
  if (Number.isNaN(started.getTime())) return '—';

  const recurrence = subscription.plan_recurrence;
  if (recurrence !== 'weekly' && recurrence !== 'monthly' && recurrence !== 'annual') {
    return formatSubscriptionDate(subscription.started);
  }

  const now = Date.now();
  let cursor = new Date(started.getTime());
  for (let i = 0; i < 520 && cursor.getTime() <= now; i += 1) {
    cursor = addSubscriptionBillingPeriod(cursor, recurrence);
  }
  return formatSubscriptionDate(cursor.toISOString());
};

/** Display label for plan_type on subscription cards */
export const formatSubscriptionPlanTypeLabel = (planType?: PlanType | null): string => {
  switch (planType) {
    case 'dual':
      return 'Covers Booking Costs';
    case 'membership':
      return 'Membership Tier';
    case 'resource':
      return 'Resource Plan';
    default:
      return '';
  }
};

/** Capitalized resource type label (e.g. kitchen → Kitchen) */
export const formatSubscriptionResourceTypeLabel = (
  resourceType?: ResourceType | null
): string => {
  if (!resourceType) return '';
  return resourceType.charAt(0).toUpperCase() + resourceType.slice(1);
};

// pending, declined, approved, verified, active, inactive, removed


const getBookingStatusColor = (status: BookingStatusType) => {
  if (status) {
    // status = status.toLowerCase();
    switch (status) {
      case "pending":
        return {primaryTextColor: "#F69E35", bgColor: '#fef5ea', secondaryTextColor: '#fce1c2'};
      case "approved":
        return {primaryTextColor: "#399f48", bgColor: '#ebf5ec', secondaryTextColor: '#afd8b5'};
      case "in_progress":
        return {primaryTextColor: "#51a0b5", bgColor: '#edf5f7', secondaryTextColor: '#a8cfda'};
      case "canceled":
        return {primaryTextColor: "#CE5D5D", bgColor: '#faeeee', secondaryTextColor: '#e6aeae'};
      case "completed":
        return {primaryTextColor: "#013847", bgColor: '#e5ebec', secondaryTextColor: '#51a0b5'};
      default:
        return {primaryTextColor: "#202124", bgColor: '#e8e8e9', secondaryTextColor: '#696969'};
    }
  }
  return {primaryTextColor: "#202124", bgColor: '#e8e8e9', secondaryTextColor: '#696969'};
};



export const getPaymentStatusColor = (status: PaymentStatusType) => {
  if (status) {
    switch (status) {
      case "requires_confirmation":
        return {primaryTextColor: "#F69E35", bgColor: '#fef5ea', secondaryTextColor: '#fce1c2', displayText: "Pending"};
      case "succeeded":
        return {primaryTextColor: "#399f48", bgColor: '#ebf5ec', secondaryTextColor: '#afd8b5', displayText: "Succeeded"};
      case "failed":
        return {primaryTextColor: "#CE5D5D", bgColor: '#faeeee', secondaryTextColor: '#e6aeae', displayText: "Failed"};
      case "canceled":
        return {primaryTextColor: "#CE5D5D", bgColor: '#faeeee', secondaryTextColor: '#e6aeae', displayText: "Canceled"};
      default:
        return {primaryTextColor: "#202124", bgColor: '#e8e8e9', secondaryTextColor: '#696969', displayText: "Unknown"};
    }
  }
  return {primaryTextColor: "#202124", bgColor: '#e8e8e9', secondaryTextColor: '#696969', displayText: "Unknown"};
};


const lightenColor = (hex: string, percent: number): string => {
  const color = parseInt(hex.replace(/^#/, ''), 16);
  const R = ((color >> 16) + percent);
  const G = (((color >> 8) & 0x00FF) + percent);
  const B = ((color & 0x0000FF) + percent);
  const newColor = ((R << 16) | (G << 8) | B).toString(16);
  return `#${newColor.padStart(6, '0')}`;
}


const darkenColor = (hex: string, percent: number): string => {
  const color = parseInt(hex.replace(/^#/, ''), 16);
  const R = ((color >> 16) - percent);
  const G = (((color >> 8) & 0x00FF) - percent);
  const B = ((color & 0x0000FF) - percent);
  const newColor = ((R << 16) | (G << 8) | B).toString(16);
  return `#${newColor.padStart(6, '0')}`;
}


const servSafeLookUp: {
  requirementCode: string;
  requirementName: string;
}[] = [
  {
    requirementCode: "FMP",
    requirementName: "Foodservice Management Professional Certification",
  },
  { requirementCode: "MFRST", requirementName: "ManageFirst" },
  {
    requirementCode: "CLNRY",
    requirementName: "National Restaurant Association Culinary Exams",
  },
  { requirementCode: "PSTRT", requirementName: "ProStart" },
  { requirementCode: "SSA", requirementName: "ServSafe Alcohol" },
  {
    requirementCode: "SSALRGNS",
    requirementName: "ServSafe Allergens Online Course",
  },
  { requirementCode: "SSFHNDLR", requirementName: "ServSafe Food Handler" },
  {
    requirementCode: "SSPRC",
    requirementName: "ServSafe Instructor and Proctor Certificates",
  },
  {
    requirementCode: "SSI",
    requirementName: "ServSafe International Food Safety",
  },
  {
    requirementCode: "PIC Ohio",
    requirementName: "ServSafe Person in Charge – Ohio",
  },
  { requirementCode: "SSWP", requirementName: "ServSafe Workplace" },
  {
    requirementCode: "SSMGR",
    requirementName: "ServSafe: Food Protection Manager Certification",
  },
  { requirementCode: "SMMR", requirementName: "Summer Institutes" },
  { requirementCode: "TXFG", requirementName: "Texas FoodGuard" },
  { requirementCode: "ID", requirementName: "Government ID" },
  { requirementCode: "PID", requirementName: "Photo ID" },
  { requirementCode: "GLI", requirementName: "General Liability Insurance" },
  { requirementCode: "HACCP", requirementName: "HACCP Certificate" },
];


type AnyObject = Record<string, any>; // A type for any object with string keys

const getChangedAttributes = <T extends AnyObject>(
  originalObj: T,
  newObj: T
): T => {
  const changedAttributes: T = {} as T;
  for (const key in newObj) {
    if (newObj.hasOwnProperty(key)) {
      if (!originalObj.hasOwnProperty(key) || newObj[key] !== originalObj[key]) {
        changedAttributes[key] = newObj[key];
      }
    }
  }
  return changedAttributes;
}

const styleThousandCommas = (number: string | number) => {
  if (typeof number === 'number'){
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  } else if (typeof number === 'string'){
    return number.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  }
  return number
}

export const stylePhoneNumber = (phone_number: string) => {
  var cleaned = ('' + phone_number).replace(/\D/g, '')
  var match = cleaned.match(/^(0-1|)?(\d{3})(\d{3})(\d{4})$/)
  // let number;
  if (match) {
      var intlCode = (match[1] ? '+1 ' : '')

      if (phone_number.length === 10){
          phone_number = [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
      } else if (phone_number.length > 3 && phone_number.length < 7){
          phone_number = [intlCode, '(', match[2], ') ', match[3]].join('');
      } else if (phone_number.length < 4){
          phone_number = [intlCode, '(', match[2], ')'].join('');
      }

    }
  return phone_number
}

/** Format 9-digit SSN as 111-22-3333; accepts digits only or mixed input. */
export const styleSSN = (ssn: string): string => {
  const digits = ('' + (ssn || '')).replace(/\D/g, '').slice(0, 9)
  if (digits.length <= 3) return digits
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`
}

const getCreditCardIcon = (paymentMethod: string) => {
  switch (paymentMethod) {
    case 'visa':
      return VisaCardIcon
    case 'mastercard':
      return MasterCardIcon
    case 'amex':
      return AmexCardIcon
    // Add cases for other card types
    default:
      return null
  }
};


export const capitalizeString = (string: string) => string?.replace(/\w/, (c) => c?.toUpperCase());

export const capitalizeWords = (str: string): string => {
  return str.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

export const getUIDFromAccessToken = (token: string | null) => {
  try {
    if (token && token.split('.').length === 3) {
      const user = JSON.parse(atob(token.split('.')[1]));
      // console.log("getUIDFromAccessToken user", user)
      return (user && user.user_id) ? user.user_id : (user && user.uid) ? user.uid : '';
    }
  } catch (e) {
    // Invalid token format
  }
  return '';
};

export const getLastPagePathFromAccessToken = (token: string | null) => {
  try {
    if (token && token.split('.').length === 3) {
      const user = JSON.parse(atob(token.split('.')[1]));
      // console.log("getLastPagePathFromAccessToken user", user)
      return (user && user.last_page_path) ? user.last_page_path : '';
    }
  } catch (e) {
    // Invalid token format
  }
  return '';
};

export const getEmailFromAccessToken = (token: string | null) => {
  try {
    if (token && token.split('.').length === 3) {
      const user = JSON.parse(atob(token.split('.')[1]));
      return user && user.email ? user.email : '';
    }
  } catch (e) {
    // Invalid token format
  }
  return '';
};

export const generateUUID = (): string => {
  return uuidv4();
}

export {
  servSafeLookUp,
  addDaysToToday,
  getDayName,
  getDayOfMonth,
  getDayOfMonthWithSuffix,
  calculateDateDifference,
  maxHours,
  // capitalizeString,
  getCreditCardIcon,
  findObjectByAttribute,
  findMemberByAttribute,
  objectToFormData,
  lightenColor,
  darkenColor,
  styleThousandCommas,
  getBookingStatusColor,
  getMembershipStatusColor,
  getSubscriptionStatusColor,
  getChangedAttributes
}



/**
 * Generates a consistent color based on a username or email string
 * @param identifier - username or email string
 * @returns hex color string
 */
export const generateUserColor = (identifier: string): string => {
  if (!identifier) return '#E6E6E6';
  
  // Create a simple hash from the string
  let hash = 0;
  for (let i = 0; i < identifier.length; i++) {
    const char = identifier.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  // Use the hash to generate consistent colors
  // Generate hue between 0-360, saturation between 40-80%, lightness between 60-80%
  const hue = Math.abs(hash) % 360;
  const saturation = 40 + (Math.abs(hash) % 40); // 40-80%
  const lightness = 60 + (Math.abs(hash) % 20); // 60-80%
  
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

/**
 * Extracts initials from first and last name
 * @param firstName - first name
 * @param lastName - last name
 * @param username - fallback to username if names not available
 * @returns 1-2 character uppercase string
 */
export const getInitials = (firstName?: string, lastName?: string, username?: string): string => {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  } else if (firstName) {
    return firstName.charAt(0).toUpperCase();
  } else if (lastName) {
    return lastName.charAt(0).toUpperCase();
  } else if (username) {
    // Extract from email or username
    const cleanUsername = username.split('@')[0]; // Remove domain if email
    return cleanUsername.charAt(0).toUpperCase();
  }
  return '?';
};

/**
 * Determines the time period type (STANDARD, PEAK, or OFF_PEAK) for a given time
 * based on the resource's time slots configuration
 */
export const getTimePeriodType = (
  time: moment.Moment,
  resourceOptions?: ResourceOptionType
): PeriodType => {
  return getTimeBlockPeriodType(time, time.clone().add(1, 'minute'), resourceOptions);
};

/**
 * Determines the time period type for a time block by checking which time slot it falls into
 * Returns the period type of the time slot that contains the start time
 */
export const getTimeBlockPeriodType = (
  startTime: moment.Moment,
  endTime: moment.Moment,
  resourceOptions?: ResourceOptionType
): PeriodType => {
  if (!resourceOptions) return 'STANDARD';
  
  const dayOfWeek = startTime.format('ddd').toLowerCase() as keyof ResourceOptionType;
  const timeSlots = resourceOptions[dayOfWeek] as TimeSlot[] | undefined;
  
  if (!timeSlots || !Array.isArray(timeSlots)) return 'STANDARD';
  
  for (const slot of timeSlots) {
    if (!slot.open_time || !slot.close_time) continue;

    const slotStart = parseClockTimeOnDate(slot.open_time, startTime);
    const slotEndRaw = parseClockTimeOnDate(slot.close_time, startTime);
    if (!slotStart || !slotEndRaw) continue;

    const slotEnd = slotEndRaw.isAfter(slotStart)
      ? slotEndRaw
      : slotEndRaw.clone().add(1, 'day');

    if (startTime.isSameOrAfter(slotStart) && startTime.isBefore(slotEnd)) {
      return (slot.period_type || 'STANDARD').toUpperCase() as PeriodType;
    }
  }
  
  return 'STANDARD';
};

/**
 * Calculates the appropriate rate for a time period
 */
export const getTimePeriodRate = (
  plan: ResourcePlanSchema | undefined,
  periodType: PeriodType
): number => {
  if (!plan) return 0;
  
  switch (periodType) {
    case 'PEAK':
      return plan.peak_rate || plan.plan_rate || 0;
    case 'OFF_PEAK':
      return plan.off_peak_rate || plan.plan_rate || 0;
    case 'STANDARD':
    default:
      return plan.plan_rate || 0;
  }
};

/**
 * Calculates the total cost for a time block with mixed time periods
 * This function splits the booking across different time periods and calculates
 * the cost for each period separately
 */
export const calculateMixedTimeBlockCost = (
  timeBlock: TimeBlock,
  plan: ResourcePlanSchema | undefined,
  resourceOptions?: ResourceOptionType
): { totalCost: number; breakdown: Array<{ period: string; hours: number; rate: number; cost: number }> } => {
  // console.log('calculateMixedTimeBlockCost - plan:', plan);
  // console.log('calculateMixedTimeBlockCost - timeBlock:', timeBlock);
  // console.log('calculateMixedTimeBlockCost - resourceOptions:', resourceOptions);
  
  if (!plan || !timeBlock) {
    // console.log('calculateMixedTimeBlockCost - Early return due to missing params');
    return { totalCost: 0, breakdown: [] };
  }
  
  const startTime = moment(timeBlock.start);
  const endTime = moment(timeBlock.end);
  
  // console.log('calculateMixedTimeBlockCost - startTime:', startTime.format('HH:mm'));
  // console.log('calculateMixedTimeBlockCost - endTime:', endTime.format('HH:mm'));
  
  // If no resource options provided, use fallback calculation
  if (!resourceOptions) {
    // console.log('calculateMixedTimeBlockCost - Using fallback calculation');
    // Fallback to simple calculation
    const duration = endTime.diff(startTime, 'hours', true);
    const periodType = getTimePeriodType(startTime, resourceOptions);
    const rate = getTimePeriodRate(plan, periodType);
    // console.log('calculateMixedTimeBlockCost - duration:', duration);
    // console.log('calculateMixedTimeBlockCost - periodType:', periodType);
    // console.log('calculateMixedTimeBlockCost - rate:', rate);
    // console.log('calculateMixedTimeBlockCost - totalCost:', rate * duration);
    return {
      totalCost: rate * duration,
      breakdown: [{ period: periodType, hours: duration, rate, cost: rate * duration }]
    };
  }
  
  const dayOfWeek = startTime.format('ddd').toLowerCase() as keyof ResourceOptionType;
  const timeSlots = resourceOptions[dayOfWeek] as TimeSlot[] | undefined;
  
  // console.log('calculateMixedTimeBlockCost - dayOfWeek:', dayOfWeek);
  // console.log('calculateMixedTimeBlockCost - timeSlots:', timeSlots);
  
  if (!timeSlots || !Array.isArray(timeSlots)) {
    // console.log('calculateMixedTimeBlockCost - Using fallback calculation');
    // Fallback to simple calculation
    const duration = endTime.diff(startTime, 'hours', true);
    const periodType = getTimePeriodType(startTime, resourceOptions);
    const rate = getTimePeriodRate(plan, periodType);
    // console.log('calculateMixedTimeBlockCost - duration:', duration);
    // console.log('calculateMixedTimeBlockCost - periodType:', periodType);
    // console.log('calculateMixedTimeBlockCost - rate:', rate);
    // console.log('calculateMixedTimeBlockCost - totalCost:', rate * duration);
    return {
      totalCost: rate * duration,
      breakdown: [{ period: periodType, hours: duration, rate, cost: rate * duration }]
    };
  }
  
  const breakdown: Array<{ period: string; hours: number; rate: number; cost: number }> = [];
  let totalCost = 0;
  
  // Sort time slots by open_time
  const sortedSlots = [...timeSlots].sort((a, b) => (a.open_time || '').localeCompare(b.open_time || ''));

  const findSlotContainingTime = (time: moment.Moment): TimeSlot | undefined =>
    sortedSlots.find((slot) => {
      if (!slot.open_time || !slot.close_time) return false;

      const slotStart = parseClockTimeOnDate(slot.open_time, startTime);
      const slotEndRaw = parseClockTimeOnDate(slot.close_time, startTime);
      if (!slotStart || !slotEndRaw) return false;

      const slotEnd = slotEndRaw.isAfter(slotStart)
        ? slotEndRaw
        : slotEndRaw.clone().add(1, 'day');

      return time.isSameOrAfter(slotStart) && time.isBefore(slotEnd);
    });
  
  // console.log('calculateMixedTimeBlockCost - sortedSlots:', sortedSlots);
  
  let currentTime = startTime.clone();
  
  // console.log('calculateMixedTimeBlockCost - Starting while loop');
  // console.log('calculateMixedTimeBlockCost - currentTime:', currentTime.format('HH:mm'));
  // console.log('calculateMixedTimeBlockCost - endTime:', endTime.format('HH:mm'));
  // console.log('calculateMixedTimeBlockCost - currentTime.isBefore(endTime):', currentTime.isBefore(endTime));
  
  while (currentTime.isBefore(endTime)) {
    const currentSlot = findSlotContainingTime(currentTime);
    
    // console.log('calculateMixedTimeBlockCost - currentSlot:', currentSlot);
    
    if (!currentSlot) {
      // console.log('calculateMixedTimeBlockCost - No matching slot, using standard rate');
      // No matching slot, use standard rate
      const remainingHours = endTime.diff(currentTime, 'hours', true);
      const rate = plan.plan_rate || 0;
      const cost = rate * remainingHours;
      // console.log('calculateMixedTimeBlockCost - remainingHours:', remainingHours);
      // console.log('calculateMixedTimeBlockCost - rate:', rate);
      // console.log('calculateMixedTimeBlockCost - cost:', cost);
      breakdown.push({ period: 'STANDARD', hours: remainingHours, rate, cost });
      totalCost += cost;
      break;
    }
    
    // Calculate the end time for this slot (either end of slot or end of booking)
    const slotStartTime = parseClockTimeOnDate(currentSlot.open_time, startTime);
    const slotEndTime = parseClockTimeOnDate(currentSlot.close_time, startTime);
    if (!slotStartTime || !slotEndTime) {
      break;
    }

    const slotEnd = slotEndTime.isAfter(slotStartTime)
      ? slotEndTime
      : slotEndTime.clone().add(1, 'day');
    
    // Use the earlier of slot end or booking end
    const actualEndTime = slotEnd.isBefore(endTime) ? slotEnd : endTime;
    
    // console.log('calculateMixedTimeBlockCost - slotEnd:', slotEnd.format('HH:mm'));
    // console.log('calculateMixedTimeBlockCost - endTime:', endTime.format('HH:mm'));
    // console.log('calculateMixedTimeBlockCost - slotEnd.toDate():', slotEnd.toDate());
    // console.log('calculateMixedTimeBlockCost - endTime.toDate():', endTime.toDate());
    // console.log('calculateMixedTimeBlockCost - slotEnd.isBefore(endTime):', slotEnd.isBefore(endTime));
    
    // If the slot end is before the booking end, we need to continue to the next period
    // This ensures we handle transitions between periods correctly
    
    const hoursInSlot = actualEndTime.diff(currentTime, 'hours', true);
    const periodType = (currentSlot.period_type || 'STANDARD').toUpperCase() as PeriodType;
    const rate = getTimePeriodRate(plan, periodType);
    const cost = rate * hoursInSlot;
    
    // console.log('calculateMixedTimeBlockCost - hoursInSlot:', hoursInSlot);
    // console.log('calculateMixedTimeBlockCost - periodType:', periodType);
    // console.log('calculateMixedTimeBlockCost - rate:', rate);
    // console.log('calculateMixedTimeBlockCost - cost:', cost);
    // console.log('calculateMixedTimeBlockCost - actualEndTime:', actualEndTime.format('HH:mm'));
    
    breakdown.push({ period: periodType, hours: hoursInSlot, rate, cost });
    totalCost += cost;
    
    currentTime = actualEndTime;
    // console.log('calculateMixedTimeBlockCost - updated currentTime:', currentTime.format('HH:mm'));
  }
  
  return { totalCost, breakdown };
};

const formatMessageCalendarDate = (date: Date, timezone: string): string =>
  new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);

const formatMessageWeekdayDate = (date: Date, timezone: string): string =>
  new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);

const getMessageCalendarKey = (value: moment.Moment, timezone: string): string =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(value.toDate());

export const getBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || TIMEZONE;
  } catch {
    return TIMEZONE;
  }
};

const isValidTimezone = (timezone: string): boolean => {
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
};

export const resolveMessageTimezone = (timezone?: string): string => {
  if (timezone && isValidTimezone(timezone)) {
    return timezone;
  }
  return getBrowserTimezone();
};

const hasTimezoneOffset = (value: string): boolean =>
  /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(value.trim());

const NAIVE_ISO_DATETIME =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?$/;

const parseNaiveUtcIsoInstant = (value: string): Date | null => {
  const match = value.trim().match(NAIVE_ISO_DATETIME);
  if (!match) return null;

  const ms = match[7] ? Number(match[7].slice(0, 3).padEnd(3, '0')) : 0;
  const date = new Date(
    Date.UTC(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3]),
      Number(match[4]),
      Number(match[5]),
      Number(match[6]),
      ms
    )
  );

  return Number.isNaN(date.getTime()) ? null : date;
};

const parseWallClockInTimezone = (value: string, timeZone: string): Date | null => {
  const match = value.trim().match(NAIVE_ISO_DATETIME);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  const second = Number(match[6]);
  const ms = 0;

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });

  const readWallClock = (instant: Date) => {
    const parts = formatter.formatToParts(instant);
    const get = (type: Intl.DateTimeFormatPartTypes) =>
      Number(parts.find((part) => part.type === type)?.value ?? '0');

    return {
      year: get('year'),
      month: get('month'),
      day: get('day'),
      hour: get('hour'),
      minute: get('minute'),
      second: get('second'),
    };
  };

  let guess = Date.UTC(year, month - 1, day, hour, minute, second, ms);

  for (let attempt = 0; attempt < 4; attempt++) {
    const wall = readWallClock(new Date(guess));
    const wallMs = Date.UTC(
      wall.year,
      wall.month - 1,
      wall.day,
      wall.hour,
      wall.minute,
      wall.second,
      ms
    );
    const desiredMs = Date.UTC(year, month - 1, day, hour, minute, second, ms);
    const diff = desiredMs - wallMs;
    if (diff === 0) return new Date(guess);
    guess += diff;
  }

  const candidate = new Date(guess);
  return Number.isNaN(candidate.getTime()) ? null : candidate;
};

export const parseMessageInstant = (created: string, timezone?: string): Date | null => {
  const trimmed = created.trim();
  if (!trimmed) return null;

  if (hasTimezoneOffset(trimmed)) {
    const date = new Date(trimmed);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const utcInstant = parseNaiveUtcIsoInstant(trimmed);
  if (utcInstant) return utcInstant;

  const tz = resolveMessageTimezone(timezone);
  return parseWallClockInTimezone(trimmed, tz);
};

export const formatMessageTime = (created?: string, timezone?: string): string => {
  if (!created) return '';
  const date = parseMessageInstant(created, timezone);
  if (!date) return '';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: getBrowserTimezone(),
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
};

export const formatMessageDateSeparator = (
  created?: string,
  referenceDate: Date = new Date(),
  timezone?: string
): string => {
  const tz = resolveMessageTimezone(timezone);
  const now = referenceDate;
  const date = created ? parseMessageInstant(created, timezone) : now;
  if (!date) {
    return formatMessageCalendarDate(now, tz);
  }

  const calendarDate = formatMessageCalendarDate(date, tz);
  const dateKey = getMessageCalendarKey(moment(date), tz);
  const todayKey = getMessageCalendarKey(moment(now), tz);

  if (dateKey === todayKey) return `Today, ${calendarDate}`;
  if (getMessageCalendarKey(moment(date).add(1, 'day'), tz) === todayKey) {
    return `Yesterday, ${calendarDate}`;
  }

  return formatMessageWeekdayDate(date, tz);
};

export const isSameMessageDay = (a?: string, b?: string, timezone?: string): boolean => {
  if (!a || !b) return false;
  const tz = resolveMessageTimezone(timezone);
  const dateA = parseMessageInstant(a, timezone);
  const dateB = parseMessageInstant(b, timezone);
  if (!dateA || !dateB) return false;

  return (
    getMessageCalendarKey(moment(dateA), tz) ===
    getMessageCalendarKey(moment(dateB), tz)
  );
};

export const STAFF_AUTO_REPLY_WINDOW_MIN_MS = 0;
export const STAFF_AUTO_REPLY_WINDOW_MAX_MS = 48 * 60 * 60 * 1000; // 48 hours

const MS_PER_HOUR = 60 * 60 * 1000;

export const msToStaffAutoReplyHours = (ms: number): number =>
  Math.round(ms / MS_PER_HOUR);

const formatStaffAutoReplyHours = (hours: number): string =>
  hours === 1 ? '1 hour' : `${hours} hours`;

export const formatStaffAutoReplyWindowLabel = (
  minAgeMs: number = STAFF_AUTO_REPLY_WINDOW_MIN_MS,
  maxAgeMs: number = STAFF_AUTO_REPLY_WINDOW_MAX_MS
): string => {
  const minHours = msToStaffAutoReplyHours(minAgeMs);
  const maxHours = msToStaffAutoReplyHours(maxAgeMs);

  if (minHours <= 0) return formatStaffAutoReplyHours(maxHours);
  if (minHours === maxHours) return formatStaffAutoReplyHours(maxHours);
  return `${minHours}-${maxHours} hours`;
};

export const getStaffAutoReplyMessage = (
  minAgeMs: number = STAFF_AUTO_REPLY_WINDOW_MIN_MS,
  maxAgeMs: number = STAFF_AUTO_REPLY_WINDOW_MAX_MS
): string =>
  `Thanks for your message. A staff member will respond within ${formatStaffAutoReplyWindowLabel(minAgeMs, maxAgeMs)}.`;

export const STAFF_AUTO_REPLY_MESSAGE = getStaffAutoReplyMessage();

export const isMessageWithinStaffAutoReplyWindow = (
  created?: string,
  referenceDate: Date = new Date(),
  timezone?: string,
  minAgeMs: number = STAFF_AUTO_REPLY_WINDOW_MIN_MS,
  maxAgeMs: number = STAFF_AUTO_REPLY_WINDOW_MAX_MS
): boolean => {
  if (!created) return false;

  const instant = parseMessageInstant(created, timezone);
  if (!instant) return false;

  const ageMs = referenceDate.getTime() - instant.getTime();
  return ageMs >= minAgeMs && ageMs <= maxAgeMs;
};

export type StaffAutoReplyMessageInput = {
  type?: 'sent' | 'received';
  content?: string;
  created?: string;
};

export const getStaffAutoReplyForMessages = (
  messages: StaffAutoReplyMessageInput[],
  autoReplyMessage: string = getStaffAutoReplyMessage(),
  timezone?: string,
  referenceDate: Date = new Date(),
  minAgeMs: number = STAFF_AUTO_REPLY_WINDOW_MIN_MS,
  maxAgeMs: number = STAFF_AUTO_REPLY_WINDOW_MAX_MS
): string | null => {
  if (!messages.length) return null;

  const last = messages[messages.length - 1];
  if (last.type !== 'received') return null;
  if (!isMessageWithinStaffAutoReplyWindow(last.created, referenceDate, timezone, minAgeMs, maxAgeMs)) {
    return null;
  }

  return autoReplyMessage;
};

export type InlineMarkdownSegment = { type: 'text' | 'bold'; value: string };
export type MarkdownLineType = 'bullet' | 'numbered' | 'heading' | 'paragraph' | 'empty';
export type MarkdownHeading = { level: 1 | 2 | 3 | 4 | 5 | 6; text: string };

export const parseMarkdownHeading = (line: string): MarkdownHeading | null => {
  const trimmed = line.trim();
  const match = trimmed.match(/^(#{1,6})\s+(.+)$/);
  if (!match) return null;

  return {
    level: match[1].length as MarkdownHeading['level'],
    text: match[2].trim(),
  };
};

export const parseInlineMarkdown = (text: string): InlineMarkdownSegment[] =>
  text.split(/(\*\*[^*]+\*\*)/g).map((part) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return { type: 'bold' as const, value: part.slice(2, -2) };
    }
    return { type: 'text' as const, value: part };
  });

export const getMarkdownLineType = (line: string): MarkdownLineType => {
  const trimmed = line.trim();
  if (!trimmed) return 'empty';
  if (parseMarkdownHeading(line)) return 'heading';
  if (trimmed.startsWith('-')) return 'bullet';
  if (/^\d+\./.test(trimmed)) return 'numbered';
  return 'paragraph';
};

export const getMarkdownHeadingClassName = (level: MarkdownHeading['level']): string => {
  switch (level) {
    case 1:
      return 'text-gray-900 font-bold text-base sm:text-[17px]';
    case 2:
      return 'text-gray-900 font-bold text-[15px] sm:text-base';
    case 3:
      return 'text-gray-900 font-semibold text-sm sm:text-[15px]';
    case 4:
      return 'text-gray-900 font-semibold text-sm';
    case 5:
      return 'text-gray-900 font-medium text-sm';
    default:
      return 'text-gray-900 font-medium text-xs sm:text-sm';
  }
};

const STRICT_DATE_FORMATS = [
  moment.ISO_8601,
  'YYYY-MM-DD',
  'YYYY-MM-DDTHH:mm:ssZ',
  'YYYY-MM-DDTHH:mm:ss.SSSZ',
];

const parseStrictCalendarDate = (value: string): moment.Moment => {
  const trimmed = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return moment(trimmed, 'YYYY-MM-DD');
  }
  return moment(trimmed, STRICT_DATE_FORMATS, true);
};

export const getCredentialExpirationInfo = (
  expiration: string | undefined | null,
  referenceDate?: moment.Moment
): CredentialExpirationInfo => {
  const trimmed = expiration?.trim();
  if (!trimmed) {
    return {
      status: 'none',
      formattedDate: null,
      daysUntilExpiration: null,
      statusLabel: null,
      detailLabel: null,
    };
  }

  const expirationDate = parseStrictCalendarDate(trimmed).startOf('day');
  if (!expirationDate.isValid()) {
    return {
      status: 'none',
      formattedDate: null,
      daysUntilExpiration: null,
      statusLabel: null,
      detailLabel: null,
    };
  }

  const now = (referenceDate ?? moment()).startOf('day');
  const daysUntil = expirationDate.diff(now, 'days');
  const formattedDate = expirationDate.format('MMM D, YYYY');

  if (daysUntil < 0) {
    return {
      status: 'expired',
      formattedDate,
      daysUntilExpiration: daysUntil,
      statusLabel: 'Expired',
      detailLabel: `Expired ${formattedDate}`,
    };
  }

  if (daysUntil <= 30) {
    const detailLabel =
      daysUntil === 0
        ? 'Expires today'
        : daysUntil === 1
          ? 'Expires in 1 day'
          : `Expires in ${daysUntil} days (${formattedDate})`;

    return {
      status: 'expiring_soon',
      formattedDate,
      daysUntilExpiration: daysUntil,
      statusLabel: 'Expiring Soon',
      detailLabel,
    };
  }

  return {
    status: 'valid',
    formattedDate,
    daysUntilExpiration: daysUntil,
    statusLabel: null,
    detailLabel: `Expires ${formattedDate}`,
  };
};

export const isRequirementEffectivelyMet = (
  requirement: Pick<CurrentRequirementsSchema, 'is_met' | 'expiration' | 'code'>,
  referenceDate?: moment.Moment
): boolean => {
  if (!requirement.is_met) return false;
  if (requirement.code === 'PAY') return true;
  const expirationInfo = getCredentialExpirationInfo(requirement.expiration, referenceDate);
  return expirationInfo.status !== 'expired';
};

export const countEffectivelyMetRequirements = (
  requirements: CurrentRequirementsSchema[],
  referenceDate?: moment.Moment
): number =>
  requirements.filter(
    (item) => item.is_required && isRequirementEffectivelyMet(item, referenceDate)
  ).length;

/**
 * Resolves a membership expand key from `?id=` (community_id value) when it matches an account membership.
 * Returns null when the query is missing or does not match any membership.
 */
export const resolveMembershipKeyFromQuery = (
  membershipQuery: string | null | undefined,
  accountMemberships?: MembershipSchema[] | null
): string | null => {
  if (!membershipQuery || !accountMemberships?.length) {
    return null;
  }
  return accountMemberships.some((membership) => membership.community_id === membershipQuery)
    ? membershipQuery
    : null;
};

/**
 * Membership card expand/lookup key: prefer a valid matching `membershipQuery`, else `community_id`.
 */
export const getMembershipCardKey = (
  membership: Pick<MembershipSchema, 'community_id'>,
  membershipQuery?: string | null
): string => {
  if (membershipQuery && membership.community_id === membershipQuery) {
    return membershipQuery;
  }
  return membership.community_id || '';
};

export type RequirementStatusTone = 'success' | 'warning' | 'error';

export type RequirementStatusDisplay = {
  expirationInfo: CredentialExpirationInfo | null;
  isEffectivelyMet: boolean;
  statusLabel: string | null;
  statusTone: RequirementStatusTone | null;
  detailLabel: string | null;
  rowSurfaceClass: string;
  rowHoverClass: string;
  subtitleTextClass: string;
  statusTextClass: string;
  iconBoxClass: string;
};

export const ORIENTATION_TRAINING_CATEGORY = 'orientation_training' as const;

export const buildRequirementStatusDisplay = (
  requirement: Pick<CurrentRequirementsSchema, 'is_met' | 'expiration' | 'code' | 'is_required' | 'presigned_url' | 'category'>,
  referenceDate?: moment.Moment,
): RequirementStatusDisplay => {
  const isPayment = requirement.code === 'PAY';
  const isStandardCredential = !isPayment;
  const isEffectivelyMet = isRequirementEffectivelyMet(requirement, referenceDate);
  const isOrientationTraining = requirement?.category?.toLowerCase() === ORIENTATION_TRAINING_CATEGORY;
  const expirationInfo =
    requirement.is_met && isStandardCredential
      ? getCredentialExpirationInfo(requirement.expiration, referenceDate)
      : null;

  let statusLabel: string | null = null;
  let statusTone: RequirementStatusTone | null = null;

  if (requirement.is_met) {
    if (expirationInfo?.status === 'expired') {
      statusLabel = expirationInfo.statusLabel;
      statusTone = 'error';
    } else if (expirationInfo?.status === 'expiring_soon') {
      statusLabel = expirationInfo.statusLabel;
      statusTone = 'warning';
    } else if (!isPayment) {
      statusLabel = isOrientationTraining ? 'Completed' : 'Approved';
      statusTone = 'success';
    }
  } else if (requirement.is_required && requirement.presigned_url) {
    statusLabel = 'Pending Approval';
    statusTone = 'warning';
  } else if (requirement.is_required) {
    statusLabel = isOrientationTraining ? 'Not Completed' : 'Not Submitted';
    statusTone = 'error';
  }

  const rowSurfaceClass =
    expirationInfo?.status === 'expired'
      ? 'border-[#F5C6CB] bg-red-50/40'
      : expirationInfo?.status === 'expiring_soon'
        ? 'border-amber-200 bg-amber-50/40'
        : 'border-[#E9E9E9] bg-white';

  const rowHoverClass =
    expirationInfo?.status === 'expired'
      ? 'hover:bg-red-50/70'
      : expirationInfo?.status === 'expiring_soon'
        ? 'hover:bg-amber-50/70'
        : 'hover:bg-gray-50';

  const detailLabel =
    requirement.is_met && isStandardCredential && expirationInfo?.detailLabel
      ? expirationInfo.detailLabel
      : null;

  const subtitleTextClass =
    expirationInfo?.status === 'expired'
      ? 'text-[#B01E1A]'
      : expirationInfo?.status === 'expiring_soon'
        ? 'text-[#d45a02]'
        : 'text-[#808080]';

  const statusTextClass =
    statusTone === 'success'
      ? 'text-[#0A7A32]'
      : statusTone === 'warning'
        ? 'text-[#d45a02]'
        : statusTone === 'error'
          ? 'text-[#B01E1A]'
          : '';

  const iconBoxClass =
    expirationInfo?.status === 'expired'
      ? 'bg-red-100'
      : expirationInfo?.status === 'expiring_soon'
        ? 'bg-amber-100'
        : 'bg-[#EFF8FA]';

  return {
    expirationInfo,
    isEffectivelyMet,
    statusLabel,
    statusTone,
    detailLabel,
    rowSurfaceClass,
    rowHoverClass,
    subtitleTextClass,
    statusTextClass,
    iconBoxClass,
  };
};

export const renderInlineMarkdown = (text: string, keyPrefix: string): React.ReactNode[] =>
  parseInlineMarkdown(text).map((segment, index) => {
    if (segment.type === 'bold') {
      return React.createElement(
        'strong',
        { key: `${keyPrefix}-bold-${index}`, className: 'font-semibold' },
        segment.value
      );
    }
    return segment.value;
  });

/** Flattens inventory items from a resource's catalog-style `resource_inventory` map */
export const getResourceInventoryItems = (
  resource?: ResourceSchema | null
): InventorySchema[] => {
  const inv = resource?.resource_inventory;
  if (!inv || typeof inv !== 'object' || Array.isArray(inv)) {
    return [];
  }

  return Object.values(inv as ResourceInventorySchema).flatMap((items) => {
    if (!Array.isArray(items)) {
      return [];
    }
    return items.filter(
      (item): item is InventorySchema =>
        Boolean(item) && typeof item === 'object' && 'item_name' in item
    );
  });
};

/** Builds an inventory_id → InventorySchema lookup from a resource */
export const buildInventoryItemsById = (
  resource?: ResourceSchema | null
): Map<string, InventorySchema> => {
  const map = new Map<string, InventorySchema>();
  getResourceInventoryItems(resource).forEach((item) => {
    if (item.id) {
      map.set(item.id, item);
    }
  });
  return map;
};

/** Resolves a display label for a plan-linked inventory selection */
export const getInventorySelectionLabel = (
  selection: PlanInventorySelection,
  inventoryItemsById: Map<string, InventorySchema>
): string => {
  const item = inventoryItemsById.get(selection.inventory_id);
  return item?.item_name?.trim() || 'Inventory item';
};

/** Whether perks, linked resources, or bundled inventory should show under plan features */
export const planHasDisplayFeatures = (
  features?: string[],
  associatedResources?: PlanResourceAssociation[]
): boolean => {
  const hasFeatures = (features?.length ?? 0) > 0;
  const hasResources = (associatedResources?.length ?? 0) > 0;
  const hasInventory = (associatedResources || []).some(
    (association) => (association.inventory?.length ?? 0) > 0
  );
  return hasFeatures || hasResources || hasInventory;
};